/**
 * The Organisation Admin Client's outer layout: the one sign-in every client
 * shares (docs/adr/40-every-elaso-client-shares-one-sign-in.md), and the socket.
 *
 * A window of this client administers one Organisation, the one its address
 * names (`/organization/$organizationId`). `/organization` alone finds the
 * caller's own. Signing in grants nothing here: the routes below ask the server
 * whether this account may administer what it opened, and a refused account
 * stays signed in.
 */
import { createFileRoute, Outlet } from "@tanstack/react-router";
import { useEffect } from "react";
import { useStore } from "zustand";
import { LoginCard } from "@/components/auth/login-card";
import { AdminLoading } from "@/components/admin/admin-status";
import { useSendToSignIn } from "@/hooks/use-send-to-sign-in";
import { isWeb } from "@/lib/platform";
import { claimSocket } from "@/lib/socket-owner";
import { authStore } from "@/lib/stores/auth-store";

export const Route = createFileRoute("/organization")({
	head: () => ({ meta: [{ title: "Organization - ELASO" }] }),
	component: OrganizationLayout,
});

function OrganizationLayout() {
	const user = useStore(authStore, (s) => s.user);
	const initialized = useStore(authStore, (s) => s.initialized);
	const login = useStore(authStore, (s) => s.login);

	useEffect(() => {
		claimSocket("organization").catch((error) => {
			console.error("Failed to claim the socket for /organization:", error);
		});
	}, []);

	// Signed out on the WEB, the login is at the Organisation's own address
	// (TECH-3350), which is the only thing that names the pool this
	// administrator's account lives in. The desktop has no address of its own
	// and keeps its login in place below.
	useSendToSignIn(isWeb);

	if (!initialized) {
		return <AdminLoading label="Loading..." />;
	}

	if (!user) {
		// On the web the hook above is navigating to the sign-in address; hold the
		// spinner rather than flash a form nobody should use.
		if (isWeb) {
			return <AdminLoading label="Loading..." />;
		}
		// The desktop's login, rendered IN PLACE and with no tenant, as it was
		// before identity homes (TECH-3482 moves it inside an identity home).
		return (
			<div className="flex h-full items-center justify-center p-4">
				<div className="w-full max-w-md">
					<LoginCard
						description="Sign in to manage your organization"
						onSubmit={login}
						sentryContext="org_auth_login"
						title="Organization"
					/>
				</div>
			</div>
		);
	}

	return <Outlet />;
}
